import Polygon from '@arcgis/core/geometry/Polygon';
import Point from '@arcgis/core/geometry/Point';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';

/**
 * Platforms supported for "open in maps" links. `apple` covers iOS and macOS
 * (Apple Maps); `google` covers Android, desktop, and everything else
 * (Google Maps, which works in any modern browser).
 */
export type MapsPlatform = 'apple' | 'google';

/**
 * User-facing preference value for the default directions/routing map.
 * `auto` defers to `detectMapsPlatform()` (device-based detection);
 * `apple`/`google` force a specific provider regardless of device.
 */
export type MapsPlatformPreference = 'auto' | MapsPlatform;

/**
 * Detects whether the current device should be sent to Apple Maps or Google
 * Maps for directions. `useDeviceDetection` (`@repo/utils`) does not
 * distinguish iOS from Android, so this is a small, local UA/platform check
 * scoped to the directions feature.
 */
export function detectMapsPlatform(): MapsPlatform {
  if (typeof navigator === 'undefined') return 'google';

  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';

  const isIOS = /iPad|iPhone|iPod/.test(userAgent) || /iPad|iPhone|iPod/.test(platform);
  // iPadOS 13+ reports as "MacIntel" with touch support; treat as iOS-ish (Apple Maps is still correct).
  const isIPadOS = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isMac = /Mac/.test(platform) && !isIPadOS && navigator.maxTouchPoints <= 1;

  return isIOS || isIPadOS || isMac ? 'apple' : 'google';
}

/**
 * Resolves a user's directions-map preference to a concrete `MapsPlatform`,
 * falling back to device detection when the preference is `auto` or unset.
 */
export function resolveMapsPlatform(preference?: MapsPlatformPreference): MapsPlatform {
  if (preference === 'apple' || preference === 'google') return preference;
  return detectMapsPlatform();
}

/**
 * Builds a directions URL for the device's default maps app, targeting the
 * given destination. The origin is intentionally omitted so the maps app
 * uses the device's current location automatically.
 */
export function getMapsDirectionsUrl(lat: number, lng: number, platform: MapsPlatform = detectMapsPlatform()): string {
  if (platform === 'apple') {
    return `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
}

/**
 * Finds the vertex of a polygon (across all rings/parts) nearest to the
 * given click point, and returns it as WGS84 { latitude, longitude }.
 *
 * The polygon and click point are expected to share the same spatial
 * reference (typically the view's Web Mercator SR). If the polygon's
 * spatial reference is already geographic (WGS84), the vertex x/y are used
 * directly without projection.
 */
export function getNearestVertexLngLat(polygon: Polygon, clickPoint: Point): { latitude: number; longitude: number } {
  const clickX = clickPoint.x;
  const clickY = clickPoint.y;

  let nearestX = clickX;
  let nearestY = clickY;
  let nearestDistSq = Infinity;

  for (const ring of polygon.rings) {
    for (const vertex of ring) {
      const [x = 0, y = 0] = vertex;
      const dx = x - clickX;
      const dy = y - clickY;
      const distSq = dx * dx + dy * dy;
      if (distSq < nearestDistSq) {
        nearestDistSq = distSq;
        nearestX = x;
        nearestY = y;
      }
    }
  }

  const isGeographic = polygon.spatialReference?.isGeographic ?? false;

  if (isGeographic) {
    return { latitude: nearestY, longitude: nearestX };
  }

  const [lng, lat] = webMercatorUtils.xyToLngLat(nearestX, nearestY);
  return { latitude: lat, longitude: lng };
}
